import type { BoardPropertyOption, BoardPropertyOptionGroup } from "@/types/board";
import { generateGroupId } from "./propertyIdGenerator";
import { isLiveOptionId } from "./propertyRefs";

export type StatusGroupRole = "todo" | "in_progress" | "complete";

/** The three fixed sections, in display order. Matching Notion: no add, rename or delete. */
export const STATUS_GROUP_ROLES: ReadonlyArray<{ role: StatusGroupRole; name: string }> = [
  { role: "todo", name: "To-do" },
  { role: "in_progress", name: "In progress" },
  { role: "complete", name: "Complete" },
];

export interface StatusSection {
  role: StatusGroupRole;
  name: string;
  options: BoardPropertyOption[];
}

/** First group claiming each option wins, matching the server's normalizer. */
function rolesByOptionId(
  groups: BoardPropertyOptionGroup[] | undefined,
): Map<string, StatusGroupRole> {
  const map = new Map<string, StatusGroupRole>();
  for (const group of groups ?? []) {
    if (!group?.role) continue;
    for (const optionId of group.optionIds ?? []) {
      if (!map.has(String(optionId))) map.set(String(optionId), group.role);
    }
  }
  return map;
}

/** Which section an option renders in. Ungrouped options fall to To-do. */
export function roleForOption(
  groups: BoardPropertyOptionGroup[] | undefined,
  optionId: string,
): StatusGroupRole {
  return rolesByOptionId(groups).get(String(optionId)) ?? "todo";
}

/**
 * The three sections to render, each keeping the options' stored order.
 *
 * A section is labelled with the STORED group's name when there is one — the
 * sprint status calls its sections Current / Future / Complete — and falls back
 * to the default label only when no group holds that role yet.
 */
export function buildStatusSections(
  options: BoardPropertyOption[] | undefined,
  groups: BoardPropertyOptionGroup[] | undefined,
): StatusSection[] {
  const roles = rolesByOptionId(groups);
  return STATUS_GROUP_ROLES.map(({ role, name }) => ({
    role,
    name: (groups ?? []).find((group) => group?.role === role)?.name || name,
    options: (options ?? []).filter(
      (option) => (roles.get(String(option.id)) ?? "todo") === role,
    ),
  }));
}

/**
 * The full three-group array to persist, minting an id for any section that has
 * none. Only role-tagged groups survive — nothing creates untagged ones yet.
 */
export function materializeStatusGroups(
  options: BoardPropertyOption[] | undefined,
  groups: BoardPropertyOptionGroup[] | undefined,
): BoardPropertyOptionGroup[] {
  const existing = groups ?? [];
  return buildStatusSections(options, groups).map((section) => {
    const prior = existing.find((group) => group?.role === section.role);
    return {
      id: prior?.id || generateGroupId(),
      role: section.role,
      name: prior?.name || section.name,
      ...(prior?.color ? { color: prior.color } : {}),
      optionIds: section.options.map((option) => String(option.id)),
    };
  });
}

/**
 * Search results split into sections for a picker. Empty sections are dropped —
 * unlike the property editor, a picker has no "+" that makes one useful. Any
 * non-status type gets a single unlabelled section, i.e. today's flat list.
 */
export function groupOptionsForPicker(
  type: string | undefined,
  options: BoardPropertyOption[],
  groups: BoardPropertyOptionGroup[] | undefined,
): Array<{ role: StatusGroupRole | null; name: string | null; options: BoardPropertyOption[] }> {
  if (type !== "status") return [{ role: null, name: null, options }];
  return buildStatusSections(options, groups)
    .filter((section) => section.options.length > 0)
    .map(({ role, name, options: sectionOptions }) => ({ role, name, options: sectionOptions }));
}

/**
 * Groups for a freshly seeded status property. Every seed site writes its
 * options as [Todo, In Progress, Done], so position is the mapping; anything
 * past the third falls to To-do.
 */
export function seedStatusGroups(
  options: ReadonlyArray<{ id: string }>,
): BoardPropertyOptionGroup[] {
  const membership: Record<StatusGroupRole, string[]> = { todo: [], in_progress: [], complete: [] };
  options.forEach((option, index) => {
    membership[STATUS_GROUP_ROLES[index]?.role ?? "todo"].push(String(option.id));
  });
  return STATUS_GROUP_ROLES.map(({ role, name }) => ({
    id: generateGroupId(),
    role,
    name,
    optionIds: membership[role],
  }));
}

/**
 * Fill in default groups for any status property that has none — the same rule
 * the server applies when it stores a datasource. Running it client-side too
 * keeps the optimistic copy identical to what will be persisted, instead of
 * rendering one flat section until the next refetch.
 */
export function seedMissingStatusGroups<T extends Record<string, any>>(
  properties: T | undefined,
): T | undefined {
  if (!properties) return properties;
  let changed = false;
  const next: Record<string, any> = {};
  for (const [propertyId, property] of Object.entries(properties)) {
    if (property?.type === "status" && !property.groups?.length) {
      next[propertyId] = { ...property, groups: seedStatusGroups(property.options ?? []) };
      changed = true;
    } else {
      next[propertyId] = property;
    }
  }
  return (changed ? next : properties) as T;
}

/**
 * Filter values that still resolve: a live option id, or a group that still
 * exists. Group ids survive deliberately — pruning must not quietly turn a
 * live group filter into a snapshot of its members.
 */
export function keepLiveFilterValues(
  property:
    | { options?: { id?: string }[]; groups?: BoardPropertyOptionGroup[] }
    | null
    | undefined,
  values: readonly (string | number)[] | undefined,
): string[] {
  const groups = property?.groups;
  return (values ?? []).map(String).filter((id) => {
    if (Array.isArray(groups) && groups.some((group) => String(group?.id) === id)) return true;
    return isLiveOptionId(property, id);
  });
}

/**
 * Expand any group ids in a filter value to the options that group holds RIGHT
 * NOW. Resolving at match time is the whole point: a filter saved against a
 * group keeps working when someone later adds an option to that group.
 *
 * Matches by group id rather than the `grp_` prefix, so an imported group
 * carrying Notion's own id resolves too.
 */
export function resolveFilterValues(
  property: { groups?: BoardPropertyOptionGroup[] } | null | undefined,
  values: readonly (string | number)[] | undefined,
): string[] {
  const ids = (values ?? []).map(String);
  const groups = property?.groups;
  if (!Array.isArray(groups) || groups.length === 0) return ids;

  const resolved: string[] = [];
  for (const id of ids) {
    const group = groups.find((candidate) => String(candidate?.id) === id);
    if (group) resolved.push(...(group.optionIds ?? []).map(String));
    else resolved.push(id);
  }
  return Array.from(new Set(resolved));
}

export interface FilterEntry {
  kind: "group" | "option";
  id: string;
  name: string;
  color?: string;
}

/**
 * The rows of a status filter's value list: each non-empty group followed by
 * its options, the way Notion lays them out. Non-status types, and a status
 * property with no groups yet, get the plain option list they have today.
 */
export function buildFilterEntries(
  type: string | undefined,
  options: BoardPropertyOption[] | undefined,
  groups: BoardPropertyOptionGroup[] | undefined,
): FilterEntry[] {
  const asOption = (option: BoardPropertyOption): FilterEntry => ({
    kind: "option",
    id: String(option.id),
    name: option.name,
    ...(option.color ? { color: option.color } : {}),
  });

  if (type !== "status") return (options ?? []).map(asOption);

  const entries: FilterEntry[] = [];
  for (const section of buildStatusSections(options, groups)) {
    if (section.options.length === 0) continue;
    const group = (groups ?? []).find((candidate) => candidate?.role === section.role);
    if (group) {
      entries.push({ kind: "group", id: String(group.id), name: group.name || section.name });
    }
    entries.push(...section.options.map(asOption));
  }
  return entries;
}

/** An option reads as checked when it is picked itself OR its group is. */
export function isFilterEntrySelected(
  groups: BoardPropertyOptionGroup[] | undefined,
  values: readonly string[],
  entryId: string,
): boolean {
  const id = String(entryId);
  if (values.includes(id)) return true;
  const owner = (groups ?? []).find((group) =>
    (group.optionIds ?? []).map(String).includes(id),
  );
  return !!owner && values.includes(String(owner.id));
}

/**
 * Toggle a group or an option in a filter value list.
 *
 * Picking a group stores the GROUP, dropping any of its members that were
 * listed individually — that is what keeps the filter live. Unticking one
 * member of a picked group collapses it to the members that remain, since the
 * group no longer describes the selection.
 */
export function toggleFilterEntry(
  groups: BoardPropertyOptionGroup[] | undefined,
  values: readonly string[],
  entryId: string,
): string[] {
  const id = String(entryId);
  const list = values.map(String);
  const all = groups ?? [];

  const group = all.find((candidate) => String(candidate.id) === id);
  if (group) {
    const members = (group.optionIds ?? []).map(String);
    const without = list.filter((value) => value !== id && !members.includes(value));
    return list.includes(id) ? without : [...without, id];
  }

  const owner = all.find((candidate) => (candidate.optionIds ?? []).map(String).includes(id));
  if (owner && list.includes(String(owner.id))) {
    const remaining = (owner.optionIds ?? []).map(String).filter((member) => member !== id);
    return [...list.filter((value) => value !== String(owner.id)), ...remaining];
  }

  return list.includes(id) ? list.filter((value) => value !== id) : [...list, id];
}

/** Same array with `optionId` moved into `role`, out of whichever section held it. */
export function groupsWithOption(
  options: BoardPropertyOption[] | undefined,
  groups: BoardPropertyOptionGroup[] | undefined,
  optionId: string,
  role: StatusGroupRole,
): BoardPropertyOptionGroup[] {
  const id = String(optionId);
  return materializeStatusGroups(options, groups).map((group) => ({
    ...group,
    optionIds:
      group.role === role
        ? group.optionIds.includes(id)
          ? group.optionIds
          : [...group.optionIds, id]
        : group.optionIds.filter((existing) => existing !== id),
  }));
}
